import { useCallback, useMemo } from "react";
import { type SetURLSearchParams, useSearchParams } from "react-router";
import type { GridRowSelectionModel } from "@mui/x-data-grid";
import { atomWithStorage } from "jotai/utils";
import { useBooleanSearchParam } from "src/hooks/useSearchParams.ts";
import { generateAtomHook } from "@utils/jotai.ts";
import {
  EDIT_TEST_SEARCH_PARAM,
  METRIC_DRAWER_BACK_SEARCH_PARAM,
  METRIC_DRAWER_SEARCH_PARAM,
  METRIC_DRAWER_TARGET_SEARCH_PARAM,
  ROW_SEARCH_PARAM,
} from "./MetricsTabView.constants";
import { decodeRowKeyFromUrl, encodeRowKeyForUrl } from "./MetricsTabView.utils";

export const metricViews = {
  add: "addMetric",
  edit: "editMetric",
} as const;

export const editMetricViewBackDestinations = {
  root: "root",
  back: "back",
} as const;

export type MetricViewsType = (typeof metricViews)[keyof typeof metricViews];
// Mirror `MetricViewsType`'s value-union shape: every consumer pattern-matches
// on values (`back === editMetricViewBackDestinations.back`, `isBackDestination`
// guard, URL param `?metric-drawer-back=back|root` carries values). Keys-union
// only happened to coincide because `back: "back"` / `root: "root"` — diverging
// the values without renaming keys would silently desync the type from runtime.
export type EditMetricViewBackDestinationsType =
  (typeof editMetricViewBackDestinations)[keyof typeof editMetricViewBackDestinations];

export type AssetMetricType = {
  metric_type: string;
  metric_group_type: string;
  metric_id: null | number;
};

export const useSelectedRow = (): [
  GridRowSelectionModel,
  (model: GridRowSelectionModel) => void,
] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = decodeRowKeyFromUrl(searchParams.get(ROW_SEARCH_PARAM));

  const model: GridRowSelectionModel = useMemo(
    () =>
      ({
        type: "include",
        ids: new Set(value ? [value] : []),
      }) as GridRowSelectionModel,
    [value]
  );

  const setModel = useCallback(
    (newModel: GridRowSelectionModel) => {
      const firstId = newModel.ids.values().next().value;

      // Write-only for clears: `?row=` persists across in-app selection clears (data still loading,
      // filter hides the row, MUI grid auto-emits empty `onRowSelectionModelChange`, toolbox
      // unmount, SparkMetricsTab reset, etc.). It is cleared with other metrics-tab query keys on
      // (a) pathname change (`useClearSearchParamsOnPathnameChange(METRICS_TAB_QUERY_PARAMS)`),
      // (b) leaving Metrics tab (`cleanupSearchParamsByTab`), or (c) asset-path change via
      // `useSetAssetPathClearingRow`.
      //
      // Uses direct `setSearchParams` (not `useSearchParamState`/QUEUE) so row writes cannot race
      // stale behind concurrent path updates from rapid tree navigation.
      if (firstId == null) return;
      setSearchParams(
        (prev) => {
          const prevObj = Object.fromEntries(prev.entries());
          const next = { ...prevObj };
          next[ROW_SEARCH_PARAM] = encodeRowKeyForUrl(String(firstId));

          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return [model, setModel];
};

export const useSelectedChartRowIds = generateAtomHook([] as (string | number)[]);

export const isFiltersFolded = atomWithStorage("isTableFilterFolded", false);

// Drawer state — encoded as URL search params (was `atomWithHash` before).
//
// Add/edit-metric drawer uses sibling params (one logical fact per key).
// Edit-test drawer: `?row=` holds the target id; `?edit-test=true` is an open flag
// only — both written in one `updateSearch` in `openFor` for atomicity (bug/4850).
// Cleanup: `METRICS_TAB_QUERY_PARAMS` strips search keys on pathname/tab change.

const updateSearch = (
  setSearchParams: SetURLSearchParams,
  mutate: (next: Record<string, string>) => void
) => {
  setSearchParams(
    (prev) => {
      const prevObj = Object.fromEntries(prev.entries());
      const next = { ...prevObj };
      mutate(next);

      return next;
    },
    { replace: true }
  );
};

/**
 * Edit-test drawer state.
 *
 * URL contract:
 * - `?row=<encoded uniqueKey>` — single source of truth for which metric row the drawer
 *   targets (same encoding as `useSelectedRow`).
 * - `?edit-test=true` — open flag only (`useBooleanSearchParam` for reads). Must stay in
 *   sync with row writes: `openFor` always sets both keys in one `updateSearch` so
 *   react-router v7 never drops one write (bug/4850). Do not set `?edit-test` via the
 *   boolean hook's setter when also touching `?row=` — its QUEUE + 10ms timer races.
 *
 * When the drawer is closed (`?edit-test` absent or not `true`), `rowUniqueKey` is
 * `null` even if `?row=` is still set (grid selection preserved).
 */
export const useEditTestDrawer = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpen] = useBooleanSearchParam(EDIT_TEST_SEARCH_PARAM);
  const rowUniqueKey = isOpen
    ? decodeRowKeyFromUrl(searchParams.get(ROW_SEARCH_PARAM))
    : null;

  const openFor = useCallback(
    (uniqueKey: string) => {
      if (!uniqueKey) {
        return;
      }
      const encoded = encodeRowKeyForUrl(uniqueKey);
      updateSearch(setSearchParams, (next) => {
        next[ROW_SEARCH_PARAM] = encoded;
        next[EDIT_TEST_SEARCH_PARAM] = "true";
      });
    },
    [setSearchParams]
  );

  const close = useCallback(() => {
    updateSearch(setSearchParams, (next) => {
      delete next[EDIT_TEST_SEARCH_PARAM];
    });
  }, [setSearchParams]);

  return { isOpen, rowUniqueKey, openFor, close };
};

const isMetricDrawerView = (v: string | null): v is MetricViewsType =>
  v === metricViews.add || v === metricViews.edit;

const isBackDestination = (
  v: string | null
): v is EditMetricViewBackDestinationsType =>
  v === editMetricViewBackDestinations.back ||
  v === editMetricViewBackDestinations.root;

const decodeAssetMetricTarget = (raw: string | null): AssetMetricType | null => {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      "metric_type" in parsed &&
      "metric_group_type" in parsed &&
      typeof (parsed as { metric_type: unknown }).metric_type === "string" &&
      typeof (parsed as { metric_group_type: unknown }).metric_group_type ===
        "string"
    ) {
      return parsed as AssetMetricType;
    }
  } catch {
    // Malformed payload (hand-edited URL, old bookmark, etc.) — treat as absent.
  }
  return null;
};

/**
 * Add/Edit-metric drawer state, encoded as 3 sibling search params:
 *   ?metric-drawer=addMetric|editMetric          (open ⇔ present)
 *   ?metric-drawer-back=back|root                (only meaningful for editMetric)
 *   ?metric-drawer-target=<json AssetMetricType> (only meaningful for editMetric)
 *
 * `openAdd`, `openEdit`, `setView`, `close` all write through one
 * `setSearchParams` call so the three keys land in the same history entry.
 * Consumers therefore never observe view='editMetric' with a missing target,
 * or vice versa.
 *
 * `target` is JSON-encoded (one logical fact = one URL value); this matches
 * the previous hash-atom payload shape exactly so no consumer needs reshaping.
 */
export const useMetricDrawer = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawView = searchParams.get(METRIC_DRAWER_SEARCH_PARAM);
  const rawBack = searchParams.get(METRIC_DRAWER_BACK_SEARCH_PARAM);
  const rawTarget = searchParams.get(METRIC_DRAWER_TARGET_SEARCH_PARAM);

  const view: MetricViewsType | null = isMetricDrawerView(rawView) ? rawView : null;
  const back: EditMetricViewBackDestinationsType | null = isBackDestination(rawBack)
    ? rawBack
    : null;
  const target = useMemo(() => decodeAssetMetricTarget(rawTarget), [rawTarget]);

  const openAdd = useCallback(() => {
    updateSearch(setSearchParams, (next) => {
      next[METRIC_DRAWER_SEARCH_PARAM] = metricViews.add;
      delete next[METRIC_DRAWER_BACK_SEARCH_PARAM];
      delete next[METRIC_DRAWER_TARGET_SEARCH_PARAM];
    });
  }, [setSearchParams]);

  const openEdit = useCallback(
    (newTarget: AssetMetricType, newBack: EditMetricViewBackDestinationsType) => {
      updateSearch(setSearchParams, (next) => {
        next[METRIC_DRAWER_SEARCH_PARAM] = metricViews.edit;
        next[METRIC_DRAWER_BACK_SEARCH_PARAM] = newBack;
        next[METRIC_DRAWER_TARGET_SEARCH_PARAM] = JSON.stringify(newTarget);
      });
    },
    [setSearchParams]
  );

  const close = useCallback(() => {
    updateSearch(setSearchParams, (next) => {
      delete next[METRIC_DRAWER_SEARCH_PARAM];
      delete next[METRIC_DRAWER_BACK_SEARCH_PARAM];
      delete next[METRIC_DRAWER_TARGET_SEARCH_PARAM];
    });
  }, [setSearchParams]);

  return { view, back, target, openAdd, openEdit, close };
};
